import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ManualBody, ManualBodyStep } from "@arc/contracts";
import { formatDate } from "@/lib/date";
import { useImagesSettled } from "./useImagesSettled.js";

/**
 * The manual print-out (ADR-031) — the legacy "Drucken": one document with the manual name as a running header and
 * "Created by … on …" as a running footer (a `<table>` thead/tfoot, the one mechanism the print engines repeat on
 * every page), then the description and every section's steps as boxed rows — the step name, its STANDARD text (the
 * server-sanitized HTML the reader renders), and one media cell: the image, or a labelled placeholder for a video
 * (no thumbnails until the worker slice) / a failed image. A checklist / question step prints its question and the
 * answers with empty circles (CSS, no icon font). Completed / failed texts stay out (legacy parity).
 *
 * Portalled to `<body>` as `.arc-print-sheet`: hidden on screen, the only thing painted on paper (`styles/base.css`).
 * Calls `onReady` once every image has settled (the caller prints), and `onAfterPrint` when the print dialog closes.
 */
export function ManualPrintSheet({ body, onReady, onAfterPrint }: { body: ManualBody; onReady: () => void; onAfterPrint: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLTableElement | null>(null);
  const settled = useImagesSettled(ref, [body]);
  const [failed, setFailed] = useState<ReadonlySet<number>>(new Set());
  useEffect(() => {
    if (settled) onReady();
  }, [settled, onReady]);
  useEffect(() => {
    window.addEventListener("afterprint", onAfterPrint, { once: true });
    return () => window.removeEventListener("afterprint", onAfterPrint);
  }, [onAfterPrint]);
  const { manual } = body;
  // Keyed by STEP, not by file id: one file may serve several steps, and a failure must swap only the row whose own
  // `<img>` errored — never its siblings (panel). The errored node has already settled the counter when it unmounts.
  const markFailed = (stepId: number) => setFailed((cur) => (cur.has(stepId) ? cur : new Set([...cur, stepId])));
  return createPortal(
    <div className="arc-print-sheet" data-kind="manual" data-testid="manual-print-sheet">
      <table className="arc-print-manual" ref={ref}>
        <thead>
          <tr>
            <td>
              <div className="arc-print-running">{manual.name}</div>
            </td>
          </tr>
        </thead>
        <tfoot>
          <tr>
            <td>
              <div className="arc-print-running">
                {t("print.createdByOn", { user: manual.creatorUsername ?? "—", date: formatDate(manual.createdAt) })}
              </div>
            </td>
          </tr>
        </tfoot>
        <tbody>
          <tr>
            <td>
              <h1>{manual.name}</h1>
              <h2>{t("print.description")}</h2>
              {manual.description ? (
                // Server-sanitized at the read boundary (ADR-013) — the same HTML the reader renders.
                <div className="arc-print-description" dangerouslySetInnerHTML={{ __html: manual.description }} />
              ) : (
                <p className="arc-print-description">{t("print.noDescription")}</p>
              )}
              {body.sections.map((section) => (
                <section key={section.id} aria-label={section.name}>
                  <h2>{section.name}</h2>
                  {section.steps.map((step) => (
                    <PrintStep key={step.id} step={step} failed={failed.has(step.id)} onImageError={markFailed} />
                  ))}
                </section>
              ))}
            </td>
          </tr>
        </tbody>
      </table>
    </div>,
    document.body,
  );
}

function PrintStep({ step, failed, onImageError }: { step: ManualBodyStep; failed: boolean; onImageError: (stepId: number) => void }) {
  const { t } = useTranslation();
  const media = step.media;
  const { question, answers } = printableAnswers(step);
  return (
    <article className="arc-print-step" aria-label={step.name}>
      <div className="arc-print-step-body">
        <h3>{step.name}</h3>
        {step.text ? <div dangerouslySetInnerHTML={{ __html: step.text }} /> : null}
        {answers ? (
          <div className="arc-print-checklist">
            {question ? <p className="arc-print-question">{question}</p> : null}
            <ul className="arc-print-answers">
              {answers.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      {media ? (
        <div className="arc-print-media">
          {media.kind === "image" && media.url && !failed ? (
            <img src={media.url} alt={media.name ?? step.name} onError={() => onImageError(step.id)} />
          ) : (
            <div className="arc-print-placeholder">
              {media.kind === "video" ? t("print.video") : media.kind === "image" ? t("print.imageUnavailable") : t("print.file")}
              {media.name ?? `#${String(media.fileId)}`}
            </div>
          )}
        </div>
      ) : null}
    </article>
  );
}

/** The question + answer lines a typed step prints (the legacy printed a checklist's text + boxes; a question's answers
 *  print the same way — plain text from the payload, never HTML). Unknown / absent payload ⇒ nothing. */
export function printableAnswers(step: ManualBodyStep): { question: string | null; answers: string[] | null } {
  const p = step.payload;
  if (!p) return { question: null, answers: null };
  switch (p.kind) {
    case "checklist":
      return { question: p.text, answers: p.items };
    case "question":
      return { question: p.question, answers: p.answers };
    case "question_with_score":
      return { question: p.question, answers: p.answers.map((a) => a.text) };
    default:
      return { question: null, answers: null };
  }
}
